import { Typography } from "antd";
import Hls from "hls.js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import playIcon from "../../../../assets/icons/jouer.svg";
import { EventType } from "../../../../helpers/types";
import { discoverService } from "../../../../services";
import { isMobile } from "../../../../utls/FunctionsUtil";
import "./index.less";

interface SimpleAudioManager {
  resetAllPlayers: () => void;
}

declare global {
  interface Window {
    globalAudioManager?: SimpleAudioManager;
  }
}

interface VideoPlayerProps {
  src: string;
  thumbnail: string;
  discoverId: string;
  globalMuted: boolean;
  index: number;
  total: number;
  onMuteChange: (muted: boolean) => void;
  loadMoreDiscovers: () => void;
  globalShowMuteButton: boolean;
  presentationOfSingleCreator: boolean;
  handleShowMuteButtonChange: (newShowMuteButtonState: boolean) => void;
  shouldShow: boolean;
}

const VolumeOnIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

const VolumeOffIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);

const INTERSECTION_THRESHOLD = 0.3;
const VIEW_TRACKING_PERCENTAGE = 25;
const LOAD_MORE_OFFSET = 5;
const PLAYBACK_RETRY_DELAY = 0;
const HLS_RETRY_DELAY = 500;
// Sequential preloading - load next video when current becomes visible
const PRELOAD_MARGIN = "0px 0px"; // No preload margin - we'll trigger manually
const PLAY_MARGIN = "-50px 0px";

//

// Video State Cache

interface VideoStateCache {
  [discoverId: string]: {
    isReady: boolean;
    timestamp: number;
    currentTime?: number; // Save playback position
  };
}

const VIDEO_CACHE_KEY = "discover_video_cache";
const CACHE_EXPIRY = 10 * 60 * 1000;

const getVideoCache = (): VideoStateCache => {
  try {
    const cached = sessionStorage.getItem(VIDEO_CACHE_KEY);
    if (cached) {
      const cache = JSON.parse(cached) as VideoStateCache;
      // Clean expired entries
      const now = Date.now();
      Object.keys(cache).forEach((key) => {
        if (now - cache[key].timestamp > CACHE_EXPIRY) {
          delete cache[key];
        }
      });
      return cache;
    }
  } catch (error) {
    // Silent fail
  }
  return {};
};

const setVideoCache = (discoverId: string, isReady: boolean, currentTime?: number) => {
  try {
    const cache = getVideoCache();
    cache[discoverId] = {
      isReady,
      timestamp: Date.now(),
      currentTime,
    };
    sessionStorage.setItem(VIDEO_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // Silent fail
  }
};

const getVideoCacheState = (discoverId: string): boolean => {
  const cache = getVideoCache();
  return cache[discoverId]?.isReady || false;
};

const getCachedPlaybackTime = (discoverId: string): number => {
  const cache = getVideoCache();
  return cache[discoverId]?.currentTime || 0;
};

const pauseAllAudioPlayers = (currentVideo?: HTMLVideoElement) => {
  if (window.globalAudioManager) {
    window.globalAudioManager.resetAllPlayers();
  }

  document.querySelectorAll("audio").forEach((audio) => {
    if (!audio.closest("video") && !audio.paused) {
      audio.pause();
    }
  });

  // Pause other videos except current one
  document.querySelectorAll("video").forEach((video) => {
    if (video !== currentVideo && !video.paused) {
      video.pause();
    }
  });
};

const configureVideoForAutoplay = (video: HTMLVideoElement) => {
  video.playsInline = true;
  video.loop = true;
  video.setAttribute("playsinline", "true");
  video.setAttribute("webkit-playsinline", "true");
  video.setAttribute("preload", "auto");
  video.setAttribute("x-webkit-airplay", "allow");

  if (video.currentTime === 0) {
    video.currentTime = 0.1;
  }
};

// const getHlsConfig = (src: string, shouldAutoStart: boolean = true): any => {
//   const isMobileDevice = isMobile();
//   console.log("[getHlsConfig] Creating HLS config for:", src);
//   console.log("[getHlsConfig] shouldAutoStart:", shouldAutoStart);

//   return {
//     debug: false,
//     enableWorker: true,
//     lowLatencyMode: false, // Disable for better buffering

//     // AGGRESSIVE buffering to prevent any freeze
//     backBufferLength: 30,
//     maxBufferLength: isMobileDevice ? 30 : 20, // Large buffer for smooth playback
//     maxMaxBufferLength: isMobileDevice ? 45 : 30,

//     // Large buffer size for aggressive preloading
//     maxBufferSize: isMobileDevice ? 30 * 1000 * 1000 : 20 * 1000 * 1000, // 30MB mobile, 20MB desktop
//     maxBufferHole: 0.5,

//     // AGGRESSIVE retry for network errors - THIS PREVENTS FREEZE
//     manifestLoadingTimeOut: 10000, // 10s timeout
//     manifestLoadingMaxRetry: 6, // Retry 6 times
//     manifestLoadingRetryDelay: 1000, // 1s between retries
//     levelLoadingTimeOut: 10000, // 10s timeout
//     levelLoadingMaxRetry: 6, // Retry 6 times
//     levelLoadingRetryDelay: 1000, // 1s between retries
//     fragLoadingTimeOut: 20000, // 20s timeout for segments
//     fragLoadingMaxRetry: 10, // Retry 10 times for segments
//     fragLoadingRetryDelay: 1000, // 1s between retries

//     // Quality selection - start with absolute lowest for fastest start
//     startLevel: 0, // Force lowest quality initially
//     capLevelToPlayerSize: true,
//     abrEwmaDefaultEstimate: 200000, // Conservative estimate
//     abrBandWidthFactor: 0.8,
//     abrBandWidthUpFactor: 0.7,

//     // Start loading based on whether this is for playback or preload
//     autoStartLoad: shouldAutoStart, // Only auto-start for visible videos
//     startPosition: -1,
//     maxFragLookUpTolerance: 0.5, // More tolerance

//     // Minimal watchdog for instant start
//     highBufferWatchdogPeriod: 0.3, // Very short watchdog
//     nudgeMaxRetry: 2,

//     // Progressive download
//     progressive: true,

//     // Minimal segments required - start playing ASAP
//     liveSyncDurationCount: 1,
//     liveMaxLatencyDurationCount: 2,

//     xhrSetup: (xhr: XMLHttpRequest, url: string) => {
//       // Extract CloudFront signature parameters from the source URL
//       const srcUrl = new URL(src, window.location.href);
//       const queryParams = srcUrl.search.substring(1); // Remove the leading '?'

//       console.log("[HLS xhrSetup] Original URL:", url);
//       console.log("[HLS xhrSetup] Query params from src:", queryParams);

//       if (queryParams) {
//         // Parse the request URL to check if it already has the signature params
//         const requestUrl = new URL(url, window.location.href);

//         // Check if the URL already has the CloudFront signature params
//         const hasSignature =
//           requestUrl.searchParams.has("Policy") && requestUrl.searchParams.has("Key-Pair-Id") && requestUrl.searchParams.has("Signature");

//         console.log("[HLS xhrSetup] Has signature already?", hasSignature);

//         if (!hasSignature) {
//           // Append CloudFront signature parameters
//           const separator = url.includes("?") ? "&" : "?";
//           const finalUrl = `${url}${separator}${queryParams}`;
//           console.log("[HLS xhrSetup] Final URL with params:", finalUrl);
//           xhr.open("GET", finalUrl, true);
//         } else {
//           // Already has signature, use as-is
//           console.log("[HLS xhrSetup] Using URL as-is (already has signature)");
//           xhr.open("GET", url, true);
//         }
//       } else {
//         console.log("[HLS xhrSetup] No query params to append");
//         xhr.open("GET", url, true);
//       }

//       xhr.withCredentials = false;
//     },

//   };
// };

const getHlsConfig = (src: string, shouldAutoStart: boolean = true): any => {
  const isMobileDevice = isMobile();

  // Extract CloudFront signature parameters from the source URL once
  let queryParams = "";
  try {
    const srcUrl = new URL(src, window.location.href);
    queryParams = srcUrl.search.substring(1); // Remove the leading '?'
  } catch (e) {
    console.error("[getHlsConfig] Failed to parse src URL:", e);
  }

  // Custom loader class that appends CloudFront signature to all requests
  const createSignedLoader = () => {
    const DefaultLoader = Hls.DefaultConfig.loader;

    return class SignedLoader extends DefaultLoader {
      constructor(config: any) {
        super(config);
        const originalLoad = this.load.bind(this);

        this.load = (context: any, config: any, callbacks: any) => {
          if (queryParams && context.url) {
            try {
              const requestUrl = new URL(context.url, window.location.href);
              const hasSignature =
                requestUrl.searchParams.has("Policy") && requestUrl.searchParams.has("Key-Pair-Id") && requestUrl.searchParams.has("Signature");

              if (!hasSignature) {
                const separator = context.url.includes("?") ? "&" : "?";
                context.url = `${context.url}${separator}${queryParams}`;
              }
            } catch (e) {
              console.error("[SignedLoader] Failed to modify URL:", e);
            }
          }
          originalLoad(context, config, callbacks);
        };
      }
    };
  };
  return {
    debug: false,
    enableWorker: true,
    lowLatencyMode: false,

    backBufferLength: 30,
    maxBufferLength: isMobileDevice ? 30 : 20,
    maxMaxBufferLength: isMobileDevice ? 45 : 30,
    maxBufferSize: isMobileDevice ? 30 * 1000 * 1000 : 20 * 1000 * 1000,
    maxBufferHole: 0.5,

    manifestLoadingTimeOut: 10000,
    manifestLoadingMaxRetry: 6,
    manifestLoadingRetryDelay: 1000,
    levelLoadingTimeOut: 10000,
    levelLoadingMaxRetry: 6,
    levelLoadingRetryDelay: 1000,
    fragLoadingTimeOut: 20000,
    fragLoadingMaxRetry: 10,
    fragLoadingRetryDelay: 1000,

    startLevel: 0,
    capLevelToPlayerSize: true,
    abrEwmaDefaultEstimate: 200000,
    abrBandWidthFactor: 0.8,
    abrBandWidthUpFactor: 0.7,

    autoStartLoad: shouldAutoStart,
    startPosition: -1,
    maxFragLookUpTolerance: 0.5,

    highBufferWatchdogPeriod: 0.3,
    nudgeMaxRetry: 2,

    progressive: true,

    liveSyncDurationCount: 1,
    liveMaxLatencyDurationCount: 2,
    loader: createSignedLoader(),
  };
};
const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  thumbnail,
  discoverId,
  globalMuted,
  index,
  total,
  onMuteChange,
  loadMoreDiscovers,
  globalShowMuteButton,
  presentationOfSingleCreator,
  handleShowMuteButtonChange,
  shouldShow,
}) => {
  const { t } = useTranslation();

  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedViewRef = useRef(false);
  const isAttemptingPlaybackRef = useRef(false);
  const hasPreloadedRef = useRef(false);
  const cachedVideoReady = getVideoCacheState(discoverId);
  const hasTriggeredNextPreloadRef = useRef(false);
  const manuallyPausedRef = useRef(false);
  const safetyTimeoutRef = useRef<number | null>(null);
  const attemptingSinceRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const suppressGlobalMuteSyncRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(!cachedVideoReady);
  const [hasError, setHasError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const [isMuted, setIsMuted] = useState(globalMuted);
  const [isVideoReady, setIsVideoReady] = useState(cachedVideoReady);
  const [userInteracted, setUserInteracted] = useState(false);
  const [shouldShowMuteButton, setShouldShowMuteButton] = useState(globalShowMuteButton);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [userClickedOnVideo, setUserClickedOnVideo] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);

  // Check if video already loaded on mount

  useEffect(() => {
    const checkVideoState = () => {
      const video = videoRef.current;
      if (video) {
        if (video.readyState >= 1) {
          setIsLoading(false);
        }
        if (video.readyState >= 2) {
          setIsVideoReady(true);
          setVideoCache(discoverId, true);

          const cachedTime = getCachedPlaybackTime(discoverId);
          if (cachedTime > 0 && video.currentTime === 0) {
            video.currentTime = cachedTime;
          }
        }
      }
    };

    checkVideoState();
    const timer = setTimeout(checkVideoState, 50);

    return () => clearTimeout(timer);
  }, [discoverId]);

  // User interaction detection for mobile autoplay

  useEffect(() => {
    const handleUserInteraction = () => {
      setUserInteracted(true);
    };

    document.addEventListener("touchstart", handleUserInteraction, { once: true, passive: true });
    document.addEventListener("click", handleUserInteraction, { once: true });

    return () => {
      document.removeEventListener("touchstart", handleUserInteraction);
      document.removeEventListener("click", handleUserInteraction);
    };
  }, []);

  // FIRST VIDEO: Load immediately on mount BEFORE DOM paints
  useEffect(() => {
    if (index === 0) {
      setIsInitialLoad(false);
      hasPreloadedRef.current = true;
      setIsPreloading(true);
      // The intersection observer will initialize the player
    } else {
      // Other videos wait for trigger from previous video
      setIsInitialLoad(false);
    }
  }, [index]);

  // Listen for preload trigger from previous video and start loading immediately
  useEffect(() => {
    const handlePreloadTrigger = (event: CustomEvent) => {
      if (event.detail.nextIndex === index && !hasPreloadedRef.current && !hlsRef.current) {
        hasPreloadedRef.current = true;
        setIsPreloading(true);
        // Start loading immediately - don't wait for intersection observer
        if (videoRef.current && !hlsRef.current) {
          const video = videoRef.current;
          if (Hls.isSupported()) {
            const hls = new Hls(getHlsConfig(src, false));
            hlsRef.current = hls;
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              hls.startLoad();
            });
            hls.on(Hls.Events.ERROR, (event, data) => {});
            hls.loadSource(src);
            hls.attachMedia(video);
          }
        }
      }
    };

    window.addEventListener("preloadNextVideo", handlePreloadTrigger as EventListener);
    return () => {
      window.removeEventListener("preloadNextVideo", handlePreloadTrigger as EventListener);
    };
  }, [index, src]);

  // Video playback management
  const startPlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isVisible || !isPageVisible) return;

    if (!video.paused && !video.ended) return;
    if (isAttemptingPlaybackRef.current) return;

    isAttemptingPlaybackRef.current = true;
    attemptingSinceRef.current = Date.now();
    if (hlsRef.current && hlsRef.current.media) {
      try {
        hlsRef.current.startLoad();
      } catch (e) {}
    }

    pauseAllAudioPlayers(video);
    video.muted = isMuted;

    // NEW APPROACH: Use loadeddata event + small delay to avoid first-frame freeze
    // This is NOT about buffer - it's about giving Safari time to prepare the video element
    let hasPlayed = false;
    let loadedDataListener: (() => void) | null = null;

    const attemptPlay = () => {
      if (!videoRef.current || !isVisible || hasPlayed) {
        isAttemptingPlaybackRef.current = false;
        attemptingSinceRef.current = null;
        return;
      }
      hasPlayed = true;
      configureVideoForAutoplay(video);
      setTimeout(() => {
        if (!videoRef.current || !isVisible) {
          isAttemptingPlaybackRef.current = false;
          attemptingSinceRef.current = null;
          return;
        }
        attemptPlayback();
      }, 100);
    };

    // Clear any previous safety timeout before scheduling a new one
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }

    // Wait for loadeddata event (means video is ready to render first frame)
    if (video.readyState >= 2) {
      // Already has data, play immediately
      attemptPlay();
    } else {
      // Wait for loadeddata event
      loadedDataListener = () => {
        if (!hasPlayed && videoRef.current && isVisible) {
          attemptPlay();
        }
      };
      video.addEventListener("loadeddata", loadedDataListener, { once: true });

      // Safety timeout - 5 seconds
      safetyTimeoutRef.current = window.setTimeout(() => {
        if (loadedDataListener) {
          video.removeEventListener("loadeddata", loadedDataListener);
        }
        if (!hasPlayed && video.readyState >= 1) {
          // Try to play anyway if we have some data

          attemptPlay();
        } else {
          isAttemptingPlaybackRef.current = false;
          attemptingSinceRef.current = null;
        }
        safetyTimeoutRef.current = null;
      }, 5000);
    }

    function attemptPlayback() {
      if (!videoRef.current || !isVisible) {
        isAttemptingPlaybackRef.current = false;
        return;
      }

      const video = videoRef.current;
      // CRITICAL FIX: Ensure video is at a safe position (not 0.0)
      // Safari mobile freezes when playing from exactly 0.0 seconds
      if (video.currentTime < 0.05) {
        video.currentTime = 0.1;
      }
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            isAttemptingPlaybackRef.current = false;
            attemptingSinceRef.current = null;
            setIsPlaying(true);
            setIsLoading(false);
            setIsVideoReady(true);
            setVideoCache(discoverId, true);
            retryCountRef.current = 0; // reset on success
          })
          .catch((error) => {
            isAttemptingPlaybackRef.current = false;
            attemptingSinceRef.current = null;
            // Fallback: if autoplay with sound is blocked, try muted autoplay locally
            const blockedByPolicy = String(error).includes("NotAllowedError");
            const v = videoRef.current;
            if (blockedByPolicy && v && !v.muted) {
              try {
                suppressGlobalMuteSyncRef.current = true;
                v.muted = true;
                setIsMuted(true);
                // Update global mute so next reels start muted until user unmutes once
                try {
                  onMuteChange(true);
                } catch (_) {}

                v.play()
                  .then(() => {
                    setIsPlaying(true);
                    setIsLoading(false);
                    setIsVideoReady(true);
                    setVideoCache(discoverId, true);
                  })
                  .catch((e2) => {});
              } catch (_) {}
            }
            // Safari often blocks autoplay - set up retry on user interaction
            if (!userInteracted) {
              const retryPlayback = () => {
                setUserInteracted(true);
                if (isVisible && videoRef.current) {
                  videoRef.current
                    .play()
                    .then(() => {
                      setIsPlaying(true);
                      setIsLoading(false);
                    })
                    .catch(() => {});
                }
              };

              // Listen for any user interaction
              document.addEventListener("click", retryPlayback, { once: true, passive: true });
              document.addEventListener("touchstart", retryPlayback, { once: true, passive: true });
            }

            setIsPlaying(false);
            setIsLoading(false);
          });
      } else {
        isAttemptingPlaybackRef.current = false;
        attemptingSinceRef.current = null;
      }
    }
  }, [isVisible, userInteracted, isMuted, isPageVisible]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || hasError) return;

    // Track that user clicked on the video
    setUserClickedOnVideo(true);

    if (isPlaying) {
      manuallyPausedRef.current = true;
      video.pause();
      setIsPlaying(false);
    } else {
      manuallyPausedRef.current = false;
      startPlayback();
    }
  }, [isPlaying, hasError, startPlayback]);

  const toggleMute = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (isMobile() && !userInteracted) return;

      const video = videoRef.current;
      if (!video) return;

      const newMuteState = !isMuted;
      setIsMuted(newMuteState);

      try {
        video.muted = newMuteState;
      } catch (error) {
        // Silent fail
      }
      onMuteChange(newMuteState);

      // If unmuting and video is playing, hide button after 2 seconds
      if (!newMuteState && isPlaying) {
        setTimeout(() => {
          setUserClickedOnVideo(false);
        }, 2000);
      }
    },
    [isMuted, onMuteChange, userInteracted, isPlaying]
  );

  // HLS player management
  const destroyPlayer = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const video = videoRef.current;
    if (video) {
      video.removeAttribute("src");
      video.load();
    }
  }, []);

  const initPlayer = useCallback(
    (isVideoVisible: boolean) => {
      const video = videoRef.current;
      if (!video || !src) return;

      if (hlsRef.current && hlsRef.current.media === video) {
        return;
      }

      // Set loading state when initializing player
      if (isVideoVisible && !isVideoReady) {
        setIsLoading(true);
      }

      try {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }

        if (Hls.isSupported()) {
          const hls = new Hls(getHlsConfig(src, isVideoVisible));
          hlsRef.current = hls;
          let lastLoadedFragmentSN: number | string = -1;
          let fragmentLoadingErrors = 0;
          let hasRetried = false;

          hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
            fragmentLoadingErrors = 0;
            lastLoadedFragmentSN = data.frag.sn;
          });

          hls.on(Hls.Events.ERROR, (_, data) => {
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
              if (data.details === Hls.ErrorDetails.FRAG_LOAD_ERROR || data.details === Hls.ErrorDetails.FRAG_LOAD_TIMEOUT) {
                fragmentLoadingErrors++;
                if (fragmentLoadingErrors > 2 && hls.levels.length > 1) {
                  const currentLevel = hls.currentLevel;
                  const newLevel = currentLevel > 0 ? currentLevel - 1 : 1;
                  hls.currentLevel = newLevel;
                  fragmentLoadingErrors = 0;
                }
              }
            }

            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  if (lastLoadedFragmentSN !== -1) {
                    hls.startLoad(lastLoadedFragmentSN as number);
                  } else {
                    hls.startLoad();
                  }
                  break;

                case Hls.ErrorTypes.MEDIA_ERROR:
                  hls.recoverMediaError();
                  break;

                default:
                  if (!hasRetried && isVisible) {
                    hasRetried = true;
                    destroyPlayer();
                    setTimeout(() => initPlayer(true), HLS_RETRY_DELAY);
                  } else {
                    setHasError(true);
                    setIsLoading(false);
                  }
                  break;
              }
            }
          });

          hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            configureVideoForAutoplay(video);
            video.muted = globalMuted;
            hls.loadSource(src);
            if (isVideoVisible) {
              hls.once(Hls.Events.MANIFEST_PARSED, () => {
                if (isVideoVisible && videoRef.current) {
                  requestAnimationFrame(() => {
                    if (isVideoVisible && videoRef.current) {
                      startPlayback();
                    }
                  });
                }
              });
            }
          });

          hls.attachMedia(video);
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
          configureVideoForAutoplay(video);
          video.muted = globalMuted;
          video.src = src;
          if (isVideoVisible) {
            video.addEventListener(
              "loadeddata",
              () => {
                if (isVideoVisible && videoRef.current) {
                  requestAnimationFrame(() => {
                    if (isVideoVisible && videoRef.current) {
                      startPlayback();
                    }
                  });
                }
              },
              { once: true }
            );
          }
        } else {
          setHasError(true);
          setIsLoading(false);
        }
      } catch (error) {
        setHasError(true);
        setIsLoading(false);
      }
    },
    [src, startPlayback, isVisible, destroyPlayer]
  );

  // Intersection observer for preloading and playback

  useEffect(() => {
    // Preload observer - start loading when triggered or visible
    const preloadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // If video 0 or triggered by previous video, start preloading
          if (entry.isIntersecting && (index === 0 || isPreloading) && !hlsRef.current) {
            initPlayer(false);
          }
        });
      },
      {
        threshold: 0,
        rootMargin: PRELOAD_MARGIN,
      }
    );

    // Play observer - start playing when visible
    const playObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isNowVisible = entry.isIntersecting;

          setIsVisible(isNowVisible);

          if (isNowVisible) {
            // Trigger preload of NEXT video immediately
            if (!hasTriggeredNextPreloadRef.current && index < total - 1) {
              hasTriggeredNextPreloadRef.current = true;
              window.dispatchEvent(
                new CustomEvent("preloadNextVideo", {
                  detail: { nextIndex: index + 1 },
                })
              );
            }

            if (index === total - LOAD_MORE_OFFSET) {
              loadMoreDiscovers();
            }

            if (hlsRef.current) {
              if (videoRef.current) {
                const video = videoRef.current;
                const isAlreadyPlaying = !video.paused && !video.ended;
                const isAttempting = isAttemptingPlaybackRef.current;

                video.muted = globalMuted;
                video.loop = true;

                // Don't auto-play if user manually paused or page is hidden
                if (!isAlreadyPlaying && !isAttempting && !manuallyPausedRef.current && isPageVisible) {
                  pauseAllAudioPlayers(video);

                  hlsRef.current.startLoad();
                  startPlayback();
                }
              }
            } else {
              // Always initialize player when visible, regardless of preload state
              // But don't auto-play if user manually paused or page is hidden
              if (!manuallyPausedRef.current && isPageVisible) {
                initPlayer(true);
              }
            }
          } else {
            if (videoRef.current && !videoRef.current.paused) {
              videoRef.current.pause();
            }
            setIsPlaying(false);
            // Reset manual pause flag when video goes out of view
            // This allows auto-play when scrolling back to this video
            manuallyPausedRef.current = false;
            // Reset click state to prevent button from appearing during scroll
            setUserClickedOnVideo(false);
            // Reset retry count for next visibility session
            retryCountRef.current = 0;
            attemptingSinceRef.current = null;
            isAttemptingPlaybackRef.current = false;
          }
        });
      },
      {
        threshold: INTERSECTION_THRESHOLD,
        rootMargin: PLAY_MARGIN,
      }
    );

    if (containerRef.current) {
      preloadObserver.observe(containerRef.current);
      playObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        preloadObserver.unobserve(containerRef.current);
        playObserver.unobserve(containerRef.current);
      }
      preloadObserver.disconnect();
      playObserver.disconnect();

      if (loadMoreDebounceRef.current) {
        clearTimeout(loadMoreDebounceRef.current);
      }
    };
  }, [initPlayer, globalMuted, index, total, loadMoreDiscovers, startPlayback, isVideoReady, isInitialLoad]);

  useEffect(() => {
    if (!isVisible || manuallyPausedRef.current || !isPageVisible) return;

    if (!hlsRef.current && videoRef.current) {
      setIsLoading(true);
      initPlayer(true);
    }
  }, [isVisible, initPlayer, isPageVisible]);

  // Safety mechanism: Ensure autoplay happens even if intersection observer misses it
  useEffect(() => {
    if (!isVisible || !hlsRef.current || !videoRef.current || !isPageVisible) return;

    const video = videoRef.current;
    const checkAutoplay = setTimeout(() => {
      // If video is visible, page is visible, player is ready, but not playing and not attempting
      if (isVisible && isPageVisible && !isPlaying && !isAttemptingPlaybackRef.current && video.paused && !manuallyPausedRef.current) {
        // Try to start playback

        hlsRef.current?.startLoad();
        startPlayback();
      }
    }, 1000); // Check after 1 second

    return () => clearTimeout(checkAutoplay);
  }, [isVisible, isPageVisible, isPlaying, startPlayback]);

  // Retry watchdog: if attempting stays true for >1.5s while visible, retry once per session
  useEffect(() => {
    if (!isVisible || !isPageVisible) return;
    const intervalId = window.setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      const attemptingSince = attemptingSinceRef.current;
      if (attemptingSince && Date.now() - attemptingSince > 1500 && video.paused && !manuallyPausedRef.current) {
        if (retryCountRef.current < 2) {
          // reset attempting and retry
          isAttemptingPlaybackRef.current = false;
          attemptingSinceRef.current = null;
          retryCountRef.current += 1;
          startPlayback();
        } else {
        }
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isVisible, isPageVisible, startPlayback]);

  // Video event listeners

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const percentage = Math.floor((video.currentTime / video.duration) * 100);
      if (percentage >= VIEW_TRACKING_PERCENTAGE && !hasTrackedViewRef.current) {
        hasTrackedViewRef.current = true;
        discoverService.updateDiscoverStats({ id: discoverId, event: EventType.VIEWS });
      }
    };

    const handleEnded = () => {
      if (video.loop) {
        video.currentTime = 0;
        video.play().catch(() => {
          setTimeout(() => video.play().catch(() => {}), PLAYBACK_RETRY_DELAY);
        });
      }
    };

    const handleCanPlay = () => {
      setIsVideoReady(true);
      setIsLoading(false);
      setVideoCache(discoverId, true); // Cache video ready state
    };

    const handleWaiting = () => {
      if (!isPlaying) setIsLoading(true);
    };

    const handlePause = () => {
      setTimeout(() => {
        if (videoRef.current?.paused) {
          setIsPlaying(false);
        }
      }, 50);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
    };

    const handlePlaying = () => {
      setIsPlaying(true);
      setIsLoading(false);
      setIsVideoReady(true);
      setVideoCache(discoverId, true);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("pause", handlePause);
    video.addEventListener("play", handlePlay);
    video.addEventListener("playing", handlePlaying);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("playing", handlePlaying);
      // Ensure attempting flag and safety timeout are cleared when detaching listeners
      isAttemptingPlaybackRef.current = false;
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
    };
  }, [discoverId, isPlaying, isVisible]);

  // Global mute state synchronization

  useEffect(() => {
    const video = videoRef.current;
    if (suppressGlobalMuteSyncRef.current) {
      // Skip one sync cycle for local muted override
      suppressGlobalMuteSyncRef.current = false;
      return;
    }
    if (video && isMuted !== globalMuted) {
      setIsMuted(globalMuted);

      try {
        video.muted = globalMuted;
      } catch (error) {
        // Silent fail
      }
    }
  }, [globalMuted, isMuted]);

  // Mute button visibility logic
  // Always show when muted (so users can unmute)
  // Show when user clicked and video is paused
  // Do NOT show during scroll if unmuted and playing

  useEffect(() => {
    const shouldShow = (isMuted && isVideoReady && isVisible) || (userClickedOnVideo && !isPlaying && isVideoReady);

    if (shouldShowMuteButton !== shouldShow) {
      setShouldShowMuteButton(shouldShow);
      requestAnimationFrame(() => {
        handleShowMuteButtonChange(shouldShow);
      });
    }
  }, [isPlaying, isMuted, isVideoReady, isVisible, userClickedOnVideo, handleShowMuteButtonChange, shouldShowMuteButton]);

  // Handle page visibility - pause when tab/browser is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      const video = videoRef.current;
      const pageIsHidden = document.hidden;

      setIsPageVisible(!pageIsHidden);

      if (!video) return;

      if (pageIsHidden) {
        if (!video.paused) {
          video.pause();
          setIsPlaying(false);
        }
        // Reset attempting flag and any pending safety timeout when page hides
        isAttemptingPlaybackRef.current = false;
        if (safetyTimeoutRef.current) {
          clearTimeout(safetyTimeoutRef.current);
          safetyTimeoutRef.current = null;
        }
      }
      // Do NOT auto-resume when page becomes visible
      // The intersection observer will handle resuming if video is still in viewport
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Cleanup on unmount

  useEffect(() => {
    return () => {
      const video = videoRef.current;
      if (video) {
        setVideoCache(discoverId, isVideoReady, video.currentTime);
        if (!video.paused) {
          video.pause();
        }
      }
    };
  }, [discoverId, isVideoReady]);

  return (
    <div ref={containerRef} className="hls-video-player" style={{ position: "relative" }}>
      {(!isVideoReady || (isLoading && !isPlaying)) && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${thumbnail})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            zIndex: 5,
            pointerEvents: "none",
          }}
        />
      )}
      {isLoading && !isPlaying && (
        <div className="loading-spinner" style={{ zIndex: 6 }}>
          <div className="spinner" />
        </div>
      )}
      <video
        ref={videoRef}
        playsInline
        loop
        muted={isMuted}
        crossOrigin="anonymous"
        preload="auto"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: isVideoReady ? 1 : 0,
          transition: "opacity 0.05s ease-in",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 6,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            cursor: "pointer",
          }}
          onClick={togglePlay}
        />
        {!isPlaying && !isLoading && !hasError && (
          <div
            className="play-icon"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <img src={playIcon} alt="play" />
          </div>
        )}
        <button
          className="mute-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleMute(e);
          }}
          aria-label={isMuted ? "Unmute" : "Mute"}
          style={{
            position: "absolute",
            top: shouldShow ? "120px" : "80px",
            right: "12px",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            color: "white",
            border: "none",
            borderRadius: "12px",
            width: "auto",
            padding: "10px",
            display: shouldShowMuteButton ? "flex" : "none",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 10,
            transition: "opacity 0.3s ease-in-out",
            opacity: shouldShowMuteButton ? 1 : 0,
          }}
        >
          {isMuted ? <VolumeOffIcon /> : <VolumeOnIcon />}
          <Typography.Text className="mute-button-text">{isMuted ? t("sound-on") : t("sound-off")}</Typography.Text>
        </button>
      </div>
    </div>
  );
};

export default React.memo(VideoPlayer, (prevProps, nextProps) => {
  return (
    prevProps.src === nextProps.src &&
    prevProps.discoverId === nextProps.discoverId &&
    prevProps.globalMuted === nextProps.globalMuted &&
    prevProps.index === nextProps.index &&
    prevProps.total === nextProps.total &&
    prevProps.globalShowMuteButton === nextProps.globalShowMuteButton &&
    prevProps.presentationOfSingleCreator === nextProps.presentationOfSingleCreator
  );
});
